import {
  ProgressBar as AriaProgressBar,
  type ProgressBarProps as AriaProgressBarProps,
} from 'react-aria-components'
import { cn } from '@/lib/utils'

// ============================================================================
// Types
// ============================================================================

export type ProgressSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type ProgressVariant = 'brand' | 'gray' | 'success' | 'warning' | 'error'
export type ProgressLabelPosition = 'top' | 'side' | 'inside' | 'none'

export interface ProgressProps extends Omit<AriaProgressBarProps, 'children'> {
  /** Size of the progress bar */
  size?: ProgressSize
  /** Color variant */
  variant?: ProgressVariant
  /** Position of the label */
  labelPosition?: ProgressLabelPosition
  /** Custom label text (overrides percentage) */
  label?: string
  /** Show percentage value */
  showValue?: boolean
  /** Additional helper text shown below */
  helperText?: string
  /** Whether the progress is indeterminate (loading) */
  isIndeterminate?: boolean
  /** Animation duration in ms */
  animationDuration?: number
  /** Custom className for the track */
  trackClassName?: string
  /** Custom className for the fill/indicator */
  fillClassName?: string
}

// ============================================================================
// Size & Variant Configurations
// ============================================================================

const sizeConfig = {
  xs: {
    track: 'h-1',
    text: 'text-xs',
    gap: 'gap-1',
  },
  sm: {
    track: 'h-1.5',
    text: 'text-xs',
    gap: 'gap-1.5',
  },
  md: {
    track: 'h-2',
    text: 'text-sm',
    gap: 'gap-1.5',
  },
  lg: {
    track: 'h-2.5',
    text: 'text-sm',
    gap: 'gap-2',
  },
  xl: {
    track: 'h-3',
    text: 'text-sm',
    gap: 'gap-2',
  },
}

const variantConfig = {
  brand: {
    fill: 'bg-brand-500',
    track: 'bg-brand-100 dark:bg-brand-900/30',
    text: 'text-brand-700 dark:text-brand-400',
  },
  gray: {
    fill: 'bg-gray-500',
    track: 'bg-gray-200 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
  },
  success: {
    fill: 'bg-success-500',
    track: 'bg-success-100 dark:bg-success-900/30',
    text: 'text-success-700 dark:text-success-400',
  },
  warning: {
    fill: 'bg-warning-500',
    track: 'bg-warning-100 dark:bg-warning-900/30',
    text: 'text-warning-700 dark:text-warning-400',
  },
  error: {
    fill: 'bg-error-500',
    track: 'bg-error-100 dark:bg-error-900/30',
    text: 'text-error-700 dark:text-error-400',
  },
}

// ============================================================================
// Progress Component
// ============================================================================

/**
 * Progress bar component following Untitled UI design patterns.
 * Built with React Aria for full accessibility support.
 *
 * @example
 * // Simple progress bar
 * <Progress value={60} />
 *
 * @example
 * // With label and helper text
 * <Progress
 *   value={75}
 *   labelPosition="top"
 *   label="Uploading..."
 *   showValue
 *   helperText="75% complete - 2 minutes remaining"
 * />
 *
 * @example
 * // Indeterminate loading state
 * <Progress isIndeterminate variant="brand" />
 */
export function Progress({
  value = 0,
  minValue = 0,
  maxValue = 100,
  size = 'md',
  variant = 'brand',
  labelPosition = 'none',
  label,
  showValue = false,
  helperText,
  isIndeterminate = false,
  animationDuration = 300,
  className,
  trackClassName,
  fillClassName,
  ...props
}: ProgressProps) {
  const config = sizeConfig[size]
  const colors = variantConfig[variant]

  // Calculate percentage for display
  const percentage = ((value - minValue) / (maxValue - minValue)) * 100
  const displayValue = `${Math.round(percentage)}%`

  // Determine if we should show label area
  const hasTopLabel = labelPosition === 'top' && (label || showValue)
  const hasSideLabel = labelPosition === 'side' && (label || showValue)
  const hasInsideLabel = labelPosition === 'inside' && showValue && size !== 'xs' && size !== 'sm'

  return (
    <AriaProgressBar
      value={isIndeterminate ? undefined : value}
      minValue={minValue}
      maxValue={maxValue}
      className={cn('flex w-full flex-col', config.gap, className)}
      {...props}
    >
      {({ percentage: ariaPercentage }) => (
        <>
          {/* Top label row */}
          {hasTopLabel && (
            <div className="flex items-center justify-between">
              {label && (
                <span className={cn('font-medium text-gray-700 dark:text-gray-300', config.text)}>
                  {label}
                </span>
              )}
              {showValue && (
                <span className={cn('font-medium', colors.text, config.text)}>
                  {displayValue}
                </span>
              )}
            </div>
          )}

          {/* Progress bar container with optional side label */}
          <div
            className={cn(
              'flex items-center',
              hasSideLabel && 'gap-3'
            )}
          >
            {/* Track */}
            <div
              className={cn(
                'relative w-full overflow-hidden rounded-full',
                colors.track,
                config.track,
                trackClassName
              )}
            >
              {/* Fill / Indicator */}
              <div
                className={cn(
                  'h-full rounded-full',
                  colors.fill,
                  isIndeterminate
                    ? 'animate-progress-indeterminate'
                    : 'transition-[width] ease-out',
                  fillClassName
                )}
                style={{
                  width: isIndeterminate ? '50%' : `${ariaPercentage ?? percentage}%`,
                  transitionDuration: isIndeterminate ? undefined : `${animationDuration}ms`,
                }}
              >
                {/* Inside label */}
                {hasInsideLabel && !isIndeterminate && percentage >= 10 && (
                  <span
                    className={cn(
                      'absolute inset-y-0 right-1 flex items-center text-white',
                      size === 'md' && 'text-[10px]',
                      size === 'lg' && 'text-xs',
                      size === 'xl' && 'text-xs font-medium'
                    )}
                  >
                    {displayValue}
                  </span>
                )}
              </div>
            </div>

            {/* Side label */}
            {hasSideLabel && (
              <span className={cn('shrink-0 font-medium', colors.text, config.text)}>
                {label || displayValue}
              </span>
            )}
          </div>

          {/* Helper text */}
          {helperText && (
            <span className="text-sm text-gray-500 dark:text-gray-400">{helperText}</span>
          )}
        </>
      )}
    </AriaProgressBar>
  )
}

// ============================================================================
// ProgressCircle Component
// ============================================================================

export interface ProgressCircleProps {
  /** Current value (0-100) */
  value?: number
  /** Size of the circle in pixels */
  size?: number
  /** Stroke width in pixels */
  strokeWidth?: number
  /** Color variant */
  variant?: ProgressVariant
  /** Show percentage in center */
  showValue?: boolean
  /** Custom label in center */
  label?: string
  /** Whether indeterminate */
  isIndeterminate?: boolean
  /** Additional className */
  className?: string
}

/**
 * Circular progress indicator following Untitled UI design patterns.
 *
 * @example
 * <ProgressCircle value={75} showValue />
 */
export function ProgressCircle({
  value = 0,
  size = 64,
  strokeWidth = 6,
  variant = 'brand',
  showValue = false,
  label,
  isIndeterminate = false,
  className,
}: ProgressCircleProps) {
  const colors = variantConfig[variant]
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (value / 100) * circumference

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg
        className={cn('transform -rotate-90', isIndeterminate && 'animate-spin')}
        width={size}
        height={size}
      >
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={cn(
            variant === 'brand' && 'stroke-brand-100 dark:stroke-brand-900/30',
            variant === 'gray' && 'stroke-gray-200 dark:stroke-gray-700',
            variant === 'success' && 'stroke-success-100 dark:stroke-success-900/30',
            variant === 'warning' && 'stroke-warning-100 dark:stroke-warning-900/30',
            variant === 'error' && 'stroke-error-100 dark:stroke-error-900/30'
          )}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className={cn(
            variant === 'brand' && 'stroke-brand-500',
            variant === 'gray' && 'stroke-gray-500',
            variant === 'success' && 'stroke-success-500',
            variant === 'warning' && 'stroke-warning-500',
            variant === 'error' && 'stroke-error-500',
            'transition-all duration-300 ease-out'
          )}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: isIndeterminate ? circumference * 0.75 : offset,
          }}
        />
      </svg>
      {/* Center label */}
      {(showValue || label) && !isIndeterminate && (
        <span
          className={cn(
            'absolute font-semibold',
            colors.text,
            size < 48 && 'text-xs',
            size >= 48 && size < 80 && 'text-sm',
            size >= 80 && 'text-base'
          )}
        >
          {label ?? `${Math.round(value)}%`}
        </span>
      )}
    </div>
  )
}

// ============================================================================
// ProgressBar Group Component (for multi-segment progress)
// ============================================================================

export interface ProgressSegment {
  value: number
  variant?: ProgressVariant
  label?: string
}

export interface ProgressGroupProps {
  /** Array of progress segments */
  segments: ProgressSegment[]
  /** Size of the progress bar */
  size?: ProgressSize
  /** Show legend below */
  showLegend?: boolean
  /** Additional className */
  className?: string
}

/**
 * Multi-segment progress bar for showing multiple categories.
 *
 * @example
 * <ProgressGroup
 *   segments={[
 *     { value: 40, variant: 'success', label: 'Complete' },
 *     { value: 25, variant: 'warning', label: 'In Progress' },
 *     { value: 35, variant: 'error', label: 'Blocked' },
 *   ]}
 *   showLegend
 * />
 */
export function ProgressGroup({
  segments,
  size = 'md',
  showLegend = false,
  className,
}: ProgressGroupProps) {
  const config = sizeConfig[size]
  const total = segments.reduce((sum, seg) => sum + seg.value, 0)

  return (
    <div className={cn('flex w-full flex-col', config.gap, className)}>
      {/* Track with segments */}
      <div
        className={cn(
          'flex w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700',
          config.track
        )}
      >
        {segments.map((segment, index) => {
          const colors = variantConfig[segment.variant ?? 'brand']
          const widthPercent = total > 0 ? (segment.value / total) * 100 : 0

          return (
            <div
              key={index}
              className={cn(
                'h-full transition-all duration-300 ease-out',
                colors.fill,
                index === 0 && 'rounded-l-full',
                index === segments.length - 1 && 'rounded-r-full'
              )}
              style={{ width: `${widthPercent}%` }}
            />
          )
        })}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {segments.map((segment, index) => {
            const colors = variantConfig[segment.variant ?? 'brand']
            return (
              <div key={index} className="flex items-center gap-1.5">
                <div className={cn('h-2 w-2 rounded-full', colors.fill)} />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {segment.label ?? `Segment ${index + 1}`}: {segment.value}%
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// CSS Animation (add to index.css if not present)
// ============================================================================
// @keyframes progress-indeterminate {
//   0% { transform: translateX(-100%); }
//   100% { transform: translateX(200%); }
// }
// .animate-progress-indeterminate {
//   animation: progress-indeterminate 1.5s ease-in-out infinite;
// }

export default Progress
